'use client';

import Link from 'next/link';
import { ArrowRight, Check } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { BrandWordmark } from '@/components/common/BrandWordmark';
import { SEO_LANDING_PAGES } from '@/lib/seo/landing-pages';
import { getSeoGuides, resolveGuideLocale } from '@/lib/seo/guides';

const SUPPORT_EMAIL = 'a13518831664@gmail.com';

type NavItem = {
  href: string;
  label: string;
};

interface PublicSiteShellProps {
  children: React.ReactNode;
}

interface PageHeroProps {
  eyebrow: string;
  title: string;
  description: string;
  primaryHref?: string;
  primaryLabel?: string;
  secondaryHref?: string;
  secondaryLabel?: string;
}

interface InfoCardProps {
  title: string;
  body: string;
}

interface FlowStepProps {
  index: number;
  title: string;
  body: string;
}

interface NoticePanelProps {
  title: string;
  children: React.ReactNode;
}

function buildNavItems(t: ReturnType<typeof useTranslations>): NavItem[] {
  return [
    { href: '/wechat-review', label: t('nav.home') },
    { href: '/pricing', label: t('nav.pricing') },
  ];
}

export function PublicSiteShell({ children }: PublicSiteShellProps) {
  const t = useTranslations('publicSite');
  const seoT = useTranslations('seoLanding');
  const locale = resolveGuideLocale(useLocale());
  const navItems = buildNavItems(t);
  const seoGuides = getSeoGuides(locale);

  return (
    <main className="h-full overflow-y-auto bg-[#F7F9FC] text-slate-950">
      <header className="sticky top-0 z-20 border-b border-slate-200/80 bg-white/92 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between md:px-6">
          <Link href="/" className="flex items-center gap-3" aria-label="DOBIDOBI">
            <BrandWordmark
              className="relative block h-[30px] w-[138px] md:h-9 md:w-[168px]"
              sizes="(max-width: 768px) 138px, 168px"
              priority
            />
            <span className="min-w-0">
              <span className="block text-[11px] font-semibold leading-4 text-slate-500">{t('shell.badge')}</span>
            </span>
          </Link>
          <nav className="flex gap-1 overflow-x-auto pb-1 md:justify-end md:pb-0" aria-label={t('shell.badge')}>
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="shrink-0 rounded-lg px-3 py-2 text-[12px] font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-950"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-10">
        {children}
      </div>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto grid max-w-6xl gap-6 px-4 py-6 text-[12px] leading-6 text-slate-500 md:grid-cols-[1fr_1.1fr_1.1fr_auto] md:px-6">
          <div>
            <p className="font-semibold text-slate-700">{t('shell.company')}</p>
            <p>{t('shell.address')}</p>
            <p>{t('shell.icp')}</p>
          </div>
          <nav aria-label={seoT('common.useCasesLabel')}>
            <p className="font-semibold text-slate-700">{seoT('common.useCasesLabel')}</p>
            <div className="mt-1 grid gap-x-4 sm:grid-cols-2">
              {SEO_LANDING_PAGES.map((page) => (
                <Link
                  key={page.path}
                  href={page.path}
                  className="text-slate-500 transition-colors hover:text-slate-950"
                >
                  {seoT(`pages.${page.key}.title`)}
                </Link>
              ))}
            </div>
          </nav>
          <nav aria-label={locale === 'en' ? 'Guides' : '指南'}>
            <p className="font-semibold text-slate-700">{locale === 'en' ? 'Guides' : '指南'}</p>
            <div className="mt-1 grid gap-x-4">
              {seoGuides.map((guide) => (
                <Link
                  key={guide.path}
                  href={guide.path}
                  className="text-slate-500 transition-colors hover:text-slate-950"
                >
                  {guide.primaryKeyword}
                </Link>
              ))}
            </div>
          </nav>
          <div className="md:text-right">
            <p>{t('shell.supportLabel')}: {SUPPORT_EMAIL}</p>
            <p>{t('shell.footerNote')}</p>
          </div>
        </div>
      </footer>
    </main>
  );
}

export function PageHero({ eyebrow, title, description, primaryHref, primaryLabel, secondaryHref, secondaryLabel }: PageHeroProps) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white px-5 py-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)] md:px-8 md:py-8">
      <div className="max-w-3xl">
        <p className="mb-3 text-[12px] font-black uppercase tracking-[0.16em] text-[#2A9EC8]">{eyebrow}</p>
        <h1 className="text-[30px] font-black leading-tight tracking-normal text-slate-950 md:text-[48px] md:leading-[1.04]">
          {title}
        </h1>
        <p className="mt-4 text-[15px] leading-7 text-slate-600 md:text-[17px] md:leading-8">{description}</p>
        {(primaryHref && primaryLabel) || (secondaryHref && secondaryLabel) ? (
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            {primaryHref && primaryLabel ? (
              <Link
                href={primaryHref}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-slate-800"
              >
                {primaryLabel}
                <ArrowRight className="h-4 w-4" />
              </Link>
            ) : null}
            {secondaryHref && secondaryLabel ? (
              <Link
                href={secondaryHref}
                className="inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50"
              >
                {secondaryLabel}
              </Link>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function InfoCard({ title, body }: InfoCardProps) {
  return (
    <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-[0_12px_36px_rgba(15,23,42,0.04)]">
      <div className="mb-4 flex h-9 w-9 items-center justify-center rounded-lg bg-[#E8F7FC] text-[#2A9EC8]">
        <Check className="h-4 w-4" />
      </div>
      <h3 className="text-[17px] font-black leading-6 text-slate-950">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
    </article>
  );
}

function FlowStep({ index, title, body }: FlowStepProps) {
  return (
    <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-[44px_1fr]">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-950 text-sm font-black text-white">
        {index}
      </div>
      <div>
        <h3 className="text-[15px] font-black leading-6 text-slate-950">{title}</h3>
        <p className="mt-1 text-sm leading-6 text-slate-600">{body}</p>
      </div>
    </div>
  );
}

export function NoticePanel({ title, children }: NoticePanelProps) {
  return (
    <section className="rounded-lg border border-[#BDECF8] bg-[#EFFBFE] p-5 text-slate-700">
      <h2 className="text-[16px] font-black text-slate-950">{title}</h2>
      <div className="mt-3 space-y-2 text-sm leading-6">{children}</div>
    </section>
  );
}

export function SectionHeading({ title, body }: { title: string; body?: string }) {
  return (
    <div className="mb-4">
      <h2 className="text-[22px] font-black leading-8 text-slate-950 md:text-[28px]">{title}</h2>
      {body ? <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">{body}</p> : null}
    </div>
  );
}

function AnchorNav({ t }: { t: ReturnType<typeof useTranslations> }) {
  const items = [
    { id: 'membership', label: t('nav.membership') },
    { id: 'benefits', label: t('nav.benefits') },
    { id: 'pricing', label: t('nav.pricing') },
    { id: 'auto-renewal', label: t('nav.autoRenewal') },
    { id: 'unsubscribe', label: t('nav.unsubscribe') },
  ];
  return (
    <nav className="mt-6 flex flex-wrap gap-2" aria-label={t('review.sectionNav')}>
      {items.map((item) => (
        <a
          key={item.id}
          href={`#${item.id}`}
          className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-600 transition-colors hover:border-[#9DE2F4] hover:text-slate-950"
        >
          {item.label}
        </a>
      ))}
    </nav>
  );
}

export function PublicReviewPage() {
  const t = useTranslations('publicSite');
  const membershipItems = ['billing', 'delivery', 'audience', 'billingSupport'];
  const membershipSteps = [1, 2, 3, 4];
  const benefitGroups = [1, 2, 3, 4, 5];
  const renewalRules = [1, 2, 3, 4, 5, 6, 7, 8];
  const cancelSteps = [1, 2, 3, 4, 5];

  return (
    <PublicSiteShell>
      <PageHero
        eyebrow={t('home.eyebrow')}
        title={t('home.title')}
        description={t('home.description')}
        primaryHref="/login?redirect=%2Fexplore"
        primaryLabel={t('review.heroCta')}
      />
      <AnchorNav t={t} />

      {/* 会员与计费 */}
      <section id="membership" className="mt-10 scroll-mt-24">
        <SectionHeading title={t('membership.summaryTitle')} body={t('membership.summaryBody')} />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {membershipItems.map((key) => (
            <InfoCard key={key} title={t(`membership.items.${key}.title`)} body={t(`membership.items.${key}.body`)} />
          ))}
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {membershipSteps.map((step) => (
            <FlowStep
              key={step}
              index={step}
              title={t(`membership.flow${step}Title`)}
              body={t(`membership.flow${step}Body`)}
            />
          ))}
        </div>
      </section>

      {/* 会员权益 */}
      <section id="benefits" className="mt-10 scroll-mt-24">
        <SectionHeading title={t('benefits.groupsTitle')} body={t('benefits.description')} />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {benefitGroups.map((group) => (
            <InfoCard key={group} title={t(`benefits.group${group}Title`)} body={t(`benefits.group${group}Body`)} />
          ))}
        </div>
      </section>

      {/* 价格 */}
      <section id="pricing" className="mt-10 scroll-mt-24">
        <SectionHeading title={t('nav.pricing')} body={t('review.pricingNote')} />
        <Link
          href="/pricing"
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-slate-800"
        >
          {t('review.pricingCta')}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      {/* 自动续费规则 */}
      <section id="auto-renewal" className="mt-10 scroll-mt-24">
        <SectionHeading title={t('autoRenewal.rulesTitle')} body={t('autoRenewal.description')} />
        <div className="grid gap-3 md:grid-cols-2">
          {renewalRules.map((rule) => (
            <FlowStep
              key={rule}
              index={rule}
              title={t(`autoRenewal.rule${rule}Title`)}
              body={t(`autoRenewal.rule${rule}Body`)}
            />
          ))}
        </div>
      </section>

      {/* 退订与解约 */}
      <section id="unsubscribe" className="mt-10 scroll-mt-24">
        <SectionHeading title={t('unsubscribe.pathTitle')} body={t('unsubscribe.description')} />
        <div className="grid gap-3 md:grid-cols-2">
          {cancelSteps.map((step) => (
            <FlowStep
              key={step}
              index={step}
              title={t(`unsubscribe.step${step}Title`)}
              body={t(`unsubscribe.step${step}Body`, { email: SUPPORT_EMAIL })}
            />
          ))}
        </div>
        <div className="mt-4">
          <NoticePanel title={t('unsubscribe.effectiveTitle')}>
            <p>{t('unsubscribe.effective1')}</p>
            <p>{t('unsubscribe.effective2')}</p>
            <p>{t('unsubscribe.effective3')}</p>
          </NoticePanel>
        </div>
      </section>

      {/* 现状与客服 */}
      <div className="mt-10">
        <NoticePanel title={t('home.statusTitle')}>
          <p>{t('home.status1')}</p>
          <p>{t('home.status2')}</p>
          <p>{t('home.status3')}</p>
          <p>{t('membership.disclosureBody')}</p>
          <p className="font-semibold text-slate-950">{t('autoRenewal.supportBody', { email: SUPPORT_EMAIL })}</p>
        </NoticePanel>
      </div>
    </PublicSiteShell>
  );
}
